<template>
  <DynLink
    v-if="to"
    :to="to"
    v-bind="$attrs"
    :animate-previous="animatePrevious"
    :animate-next="animateNext"
    :class="[
      btnClasses,
      expand
        ? `
          focus-default
          after:absolute after:inset-0
          focus-visible:after:ring-1 focus-visible:after:ring-focusTeacher focus-visible:after:ring-offset-4
        `
        : '',
    ]"
    :target="
      target || (typeof to === 'string' && isExternalUrl(to))
        ? '_blank'
        : '_self'
    "
  >
    <Component
      :is="'Icon' + (iconLeft || 'Chevron')"
      v-if="iconLeft || back"
      :class="[
        iconClasses,
        back
          ? 'transition group-hover:translate-x-[-2px] group-focus-visible:translate-x-[-2px]'
          : '',
      ]"
    />
    <slot />
    <Component :is="'Icon' + iconRight" v-if="iconRight" :class="iconClasses" />
  </DynLink>
  <button
    v-else
    :class="btnClasses"
    :type="type"
    v-bind="$attrs"
  >
    <Component
      :is="'Icon' + (iconLeft || 'Chevron')"
      v-if="iconLeft || back"
      :class="[
        iconClasses,
        back
          ? 'transition group-hover:translate-x-[-2px] group-focus-visible:translate-x-[-2px]'
          : '',
      ]"
    />
    <slot />
    <Component :is="'Icon' + iconRight" v-if="iconRight" :class="iconClasses" />
  </button>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const props = withDefaults(
  defineProps<{
    to?: string | RouteLocationRaw | null
    target?: string
    iconLeft?: string
    iconRight?: string
    iconClasses?: string
    type?: 'button' | 'submit' | 'reset'
    variant?: 'sus' | 'big' | 'regular' | 'clean' | 'outline' | 'none'
    action?: 'danger' | 'regular'
    back?: boolean
    expand?: boolean
    animateNext?: boolean
    animatePrevious?: boolean
  }>(),
  {
    iconClasses: 'w-2',
    type: 'button',
    action: 'regular',
    variant: 'regular',
    expand: false,
    animateNext: false,
    animatePrevious: false,
  },
)

// The Base style for the button
const base = 'inline-flex gap-2 transition duration-200 whitespace-nowrap items-center group rounded-full'

// The focus behavior for the button
const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'

// The size of the text and the corresponding padding
const size = {
  none: '',
  sus: 'typo-body-xl-semibold lg:typo-heading-m-semibold px-5 lg:px-8 py-3 lg:py-5',
  big: 'md:text-[1.25rem] text-base font-semibold px-8 py-3',
  clean: 'md:text-lg sm:text-base text-sm px-2 -mx-2',
  DEFAULT: 'md:text-lg sm:text-base text-sm font-semibold px-5 py-2',
}

// The text, background and focus-ring color for the button
const color = {
  none: {
    regular: '',
    danger: '',
  },
  outline: {
    regular:
      'hover:bg-surface-500 focus-visible:bg-surface-500 text-violet-text teacher:focus-visible:ring-focusTeacher student:focus-visible:ring-focusStudent ring-2 ring-violet-700',
    danger:
      'hover:bg-surface-500 focus-visible:bg-notification-negative/10 text-notification-negative teacher:focus-visible:ring-focusTeacher student:focus-visible:ring-focusStudent ring-2 hover:ring-notification-negative-hover ring-notification-negative',
  },
  clean: {
    regular:
      'hover:text-violet-text-dark text-violet-text ring-current font-semibold',
    danger:
      'text-notification-negative hover:text-notification-negative-hover ring-current',
  },
  DEFAULT: {
    regular:
      'bg-notification-success hover:bg-notification-success-hover text-violet-text-dark focus-visible:ring-notification-success-hover disabled:!bg-notification-success/30 disabled:!text-violet-text-dark/40',
    danger:
      'bg-notification-negative hover:bg-notification-negative-hover text-white focus-visible:ring-notification-negative-hover',
  },
}

const btnClasses = computed(() => {
  // Maby js map would be better here but currently there es no way to map.setDefault
  const colorKey = Object.keys(color).includes(props.variant)
    ? (props.variant as keyof typeof color)
    : 'DEFAULT'

  const sizeKey = Object.keys(size).includes(props.variant)
    ? (props.variant as keyof typeof size)
    : 'DEFAULT'

  return [color[colorKey][props.action], size[sizeKey], base, focus]
})
</script>
